import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";

export default function Cart() {
  const { items, update, remove, subtotal } = useCart();
  const { format } = useCurrency();
  const nav = useNavigate();
  const shipping = subtotal >= 50 || subtotal === 0 ? 0 : 4.99;

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-28 text-center">
        <ShoppingBag size={48} className="mx-auto text-zinc-300" />
        <h1 className="font-display text-3xl font-bold mt-6">Your cart is empty</h1>
        <p className="text-zinc-500 mt-2">Discover something you'll love.</p>
        <Button asChild className="rounded-full mt-6" data-testid="empty-cart-shop"><Link to="/shop">Continue shopping</Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-12">
      <h1 className="font-display text-4xl font-bold tracking-tight mb-10">Shopping Cart</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-6">
          {items.map((i) => (
            <div key={i.key} className="flex gap-4 border-b border-zinc-100 pb-6" data-testid={`cart-item-${i.slug}`}>
              <Link to={`/product/${i.slug}`} className="w-24 h-32 bg-zinc-100 rounded-lg overflow-hidden shrink-0">
                <img src={i.image} alt={i.title} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1">
                <div className="flex justify-between">
                  <div>
                    <Link to={`/product/${i.slug}`} className="font-medium hover:text-zinc-500">{i.title}</Link>
                    {i.variant_label && <p className="text-xs text-zinc-500 mt-0.5">{i.variant_label}</p>}
                  </div>
                  <button onClick={() => remove(i.key)} className="text-zinc-400 hover:text-red-600" data-testid={`remove-${i.slug}`}><Trash2 size={17} /></button>
                </div>
                <div className="flex justify-between items-end mt-4">
                  <div className="flex items-center border border-zinc-300 rounded-full">
                    <button onClick={() => update(i.key, i.quantity - 1)} className="p-2.5" data-testid={`dec-${i.slug}`}><Minus size={13} /></button>
                    <span className="w-8 text-center text-sm">{i.quantity}</span>
                    <button onClick={() => update(i.key, i.quantity + 1)} className="p-2.5" data-testid={`inc-${i.slug}`}><Plus size={13} /></button>
                  </div>
                  <span className="font-semibold">{format(i.price * i.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-zinc-50 rounded-xl p-6 sticky top-28">
            <h2 className="font-display text-xl font-bold mb-5">Order Summary</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>{format(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Shipping</span><span>{shipping === 0 ? "Free" : format(shipping)}</span></div>
              {subtotal < 50 && <p className="text-xs text-zinc-400">Add {format(50 - subtotal)} more for free shipping</p>}
              <div className="border-t border-zinc-200 pt-3 flex justify-between font-semibold text-base">
                <span>Total</span><span data-testid="cart-total">{format(subtotal + shipping)}</span>
              </div>
            </div>
            <Button onClick={() => nav("/checkout")} className="w-full rounded-full h-12 mt-6" data-testid="checkout-btn">
              Checkout <ArrowRight size={16} className="ml-1" />
            </Button>
            <Link to="/shop" className="block text-center text-sm text-zinc-500 hover:text-zinc-900 mt-4">Continue shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
